import { config, customOrderUTMParameters, reusablePaymentMethods } from '@/lib/config';
import { Context } from '@/components/Store';
import { deleteProperty } from 'dot-prop';
import { error as errorLog, log } from '@/lib/log';
import { getAccountSubscriptions } from '@/lib/http';
import { useContext } from 'react';
import auth from '../lib/authentication';
import cleanDeep from 'clean-deep';
import type { AccountSubscription } from '@/lib/util.d';
import type { AuthSession, CognitoGroups } from '@/types/index.d';
import type { Bundle, ContractCustomParameter, Selection, State, Voucher } from '@/components/Store.d';
import type { PurchaseResponse } from '@/components/Approve/Approve.d';

/**
 *
 * @param {number} start
 * @param {number} end
 * @param {number} step
 * @returns {array}
 */
function range(start: number, end: number, step = 1) {
  const len = Math.floor((end - start) / step) + 1;

  return Array(len)
    .fill(0)
    .map((_, idx) => start + idx * step);
}

/**
 *
 * @param {Array<Record<string, number>>} inputArray
 * @param {string} primaryKey
 * @param {string} secondaryKey
 * @returns {array}
 */
function sortObject(inputArray: Array<Record<string, number>>, primaryKey: string, secondaryKey?: string) {
  const outputArray = inputArray.slice(0);

  return outputArray.sort((a, b) => {
    const aPrimary = a[primaryKey];
    const bPrimary = b[primaryKey];
    const aSecondary = secondaryKey ? a[secondaryKey] : undefined;
    const bSecondary = secondaryKey ? b[secondaryKey] : undefined;
    // Allow undefined to produce NaN in subtraction, which sorts to end (original JS behavior)
    return (aPrimary as number) - (bPrimary as number) || (aSecondary !== undefined && bSecondary !== undefined ? aSecondary - bSecondary : 0);
  });
}

/**
 *
 * @param {State} state
 */
function persistState(state: State | Partial<State>) {
  state = cleanDeep(state);
  deleteProperty(state, 'user.password');

  const currentSession = cleanDeep(JSON.parse(sessionStorage.getItem('currentSession') || '{}')) || {};

  sessionStorage.setItem(
    'sessionData',
    JSON.stringify({
      ...currentSession.state,
      ...state
    })
  );
}

/**
 * Gets object key by value
 * @param {Record<string, unknown>} object - Object to search
 * @param {string} value - Value to find
 * @returns {string} Key matching the value
 */
function getKeyByValue(object: Record<string, unknown>, value: string): string {
  return Object.keys(object).find((key) => object[key] === value) || '';
}

/**
 * Gets object key by value in array
 * @param {Record<string, Array<unknown>>} object - Object with array values
 * @param {unknown} value - Value to find in arrays
 * @returns {unknown} Key matching the value
 */
function getKeyByValueArray(object: Record<string, Array<unknown>>, value: unknown): unknown {
  return Object.keys(object).find((key) => object[key]?.includes(value));
}

/**
 * Receives URLSearchParams and saves all occuring UTM Parameters to the sessionStorage.
 * UTM Parameters can have the prefix utm_ or dst_ depending on the referer
 * @param {URLSearchParams} urlParams - URL search parameters
 * @returns {void}
 */
function saveUTMParamsInSessionStorage(urlParams: URLSearchParams) {
  const utmParams: Record<string, string> = {};

  for (const [key, value] of urlParams.entries()) {
    if (key.startsWith('dst_') || key.startsWith('utm_')) {
      const prefix = key.includes('dst_') ? 'dst_' : 'utm_';
      const parts = key.split(prefix);
      const suffix = parts[1];
      if (suffix) {
        const newKey = `utm${suffix.charAt(0).toUpperCase()}${suffix.slice(1)}`;
        utmParams[newKey] = value;
      }
    }
  }

  if (Object.keys(utmParams).length) {
    sessionStorage.setItem('utm', JSON.stringify({ ...utmParams }));
  }
}

/**
 * Checks if there are UTM Parameters saved in sessionStorage and returns them
 * as an object.
 *
 * @returns {Object} holding utm params from sessionStorage (eg: {"utmSource": "Standard"})
 */
function getUTMParamsFromSessionStorage(): Partial<{
  utmTerm: string;
  utmContent: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
}> {
  const foundUTMParameters: Record<string, string> = {};
  const utmParams = JSON.parse(sessionStorage.getItem('utm') ?? '{}');

  if (utmParams) {
    customOrderUTMParameters.forEach((utm) => {
      const item = utmParams[utm];

      if (item) {
        foundUTMParameters[utm] = utmParams[utm];
      }
    });
  }

  return foundUTMParameters;
}

/**
 *
 * @param {Array|String} urlParams
 * @returns {string}
 */
function getUrlParams(urlParams: string[] | string) {
  urlParams = typeof urlParams === 'string' ? [urlParams] : urlParams;

  const parsedUrl = new URL(window.location.href);
  const searchParams = new URLSearchParams(parsedUrl.search);
  const parsedQueryStrings = Object.fromEntries(searchParams.entries());

  const filteredParams = Object.keys(parsedQueryStrings)
    .filter((key) => urlParams.includes(key))
    .reduce((obj, key) => {
      return {
        ...obj,
        [key]: parsedQueryStrings[key]
      };
    }, {});

  return filteredParams;
}

/**
 * Detect whether input is of type Array
 * @param {unknown} input
 * @returns {boolean}
 */
function isArray(input: unknown): input is unknown[] {
  return Array.isArray(input);
}

/**
 * Detect whether input is of type Object (plain object only, not arrays, dates, etc.)
 * @param {unknown} input
 * @returns {boolean}
 */
function isObject(input: unknown): input is Record<string, unknown> {
  return Object.prototype.toString.call(input) === '[object Object]';
}

/**
 * Detect whether input is of type String (including String objects)
 * @param {unknown} input
 * @returns {boolean}
 */
function isString(input: unknown): input is string {
  return typeof input === 'string' || Object.prototype.toString.call(input) === '[object String]';
}

/**
 *
 * @param {string | string[]} fragmentTitle
 */
function setTitle(fragmentTitle: string | string[]) {
  let title = Array.isArray(fragmentTitle) ? fragmentTitle.join(' - ') : fragmentTitle;
  title = `${title} - derStandard.at`;

  document.title = title;

  const meta = document.querySelector('meta[name=description]');
  meta?.setAttribute('content', title);
}

/**
 *
 * @param {Bundle} bundleData
 * @returns {string}
 */
function getProductClass(bundleData: Bundle) {
  if (!bundleData || !Object.prototype.hasOwnProperty.call(bundleData, 'containsPhysical')) {
    return '';
  }

  const { containsPhysical } = bundleData;

  if (isPur(bundleData)) {
    return containsPhysical ? 'product-purwochenende' : 'product-pur';
  } else if (isSupporter(bundleData)) {
    return 'supporter';
  } else if (isSmart(bundleData)) {
    return 'product-smart';
  }

  return '';
}

/**
 * Formats product headlines
 * @param {string} headline
 * @returns {string}
 */
function formatHeadline(headline: string) {
  let htmlHeadline;

  headline = headline
    .replace(/DER STANDARD/g, 'DER&nbsp;STANDARD') // adds a non-breaking space for 'DER STANDARD'
    .replace(/PUR Jahresabo/g, 'PUR&nbsp;Jahresabo') // adds a non-breaking space for 'PUR Jahresabo'
    .replace(/\s\+\s/g, '+'); // removes spaces around 'PUR + Wochenende'

  if (headline.includes(' ')) {
    htmlHeadline = headline.split(' ').map((line, idx) => {
      return idx === 0 ? `<span><em><small>${line}</small></em></span>` : `<span>${line}</span>`;
    });
  }

  return Array.isArray(htmlHeadline) ? htmlHeadline.join('') : `<span>${headline}</span>`;
}

/**
 *
 * @param {Selection} selection
 * @returns {string}
 */
function setSupporterAmount(selection: Selection) {
  let period;

  switch (selection.period) {
    case 'single':
      period = 'Einmalig';
      break;

    case 'monthly':
      period = 'Monatlich';
      break;

    case 'yearly':
      period = 'Jährlich';
      break;

    default:
      period = '';
      break;
  }

  return ` ${period} ${selection.tier && parseInt(selection.tier, 10) ? selection.tier : 0}€`;
}

/**
 * Returns the value in the specified array that corresponds to the name
 * @param {array} params
 * @param {string} name
 * @returns {boolean}
 */
function getCustomParameter(params: Array<ContractCustomParameter>, name: string): string | null {
  const result = params.find((el) => el.parameterName === name);
  return result && result.parameterValue ? result.parameterValue : null;
}

/**
 *
 * @param {String} trialUnit
 * @param {Number|String} count
 * @returns {String}
 */
function getTrialPeriod(trialUnit: string, count = 1) {
  switch (trialUnit.toLowerCase()) {
    case 'minute':
    case 'minutes':
      return isSingle(count) ? 'Minute' : 'Minuten';

    case 'hour':
    case 'hours':
      return isSingle(count) ? 'Stunde' : 'Stunden';

    case 'day':
    case 'days':
      return isSingle(count) ? 'Tag' : 'Tage';

    case 'month':
    case 'months':
      return isSingle(count) ? 'Monat' : 'Monate';

    case 'year':
    case 'years':
      return isSingle(count) ? 'Jahr' : 'Jahre';

    default:
      throw Error('Unsupported period specified');
  }
}

/**
 * Formats a price according to Austrian locale. The second parameter is optional and controls whether
 * to print the fraction (e.g. 12,00) or not (e.g. 12,-).
 * @param {String} price
 * @param {Boolean} omitFraction
 * @returns {string}
 */
function formatPrice(price: string, omitFraction = false) {
  const enDash = '&#8211;';

  const formattedPrice = Math.abs(parseFloat(price))
    .toLocaleString('de-AT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
    .replace('€', 'EUR');

  return omitFraction ? formattedPrice.replace(',00', `,${enDash}`).trim() : formattedPrice;
}
/**
 *
 * @param {Bundle | null} bundleData
 * @param {Boolean} eligibleForTrial
 * @param {Object} voucher
 * @returns {String}
 */
function getPricingText(bundleData: Bundle | null, eligibleForTrial: boolean, voucher?: Voucher) {
  if (!bundleData) {
    return '';
  }

  const { sumGrossAmount, sumTrialGrossAmount } = bundleData;
  const trialEnabled = (eligibleForTrial && bundleData.contractTrialInfo?.trialEnabled) || false;

  const trialUnit = trialEnabled ? bundleData.contractTrialInfo.trialUnit : bundleData.contractInfo.contractUnit;

  const trialPeriod = trialEnabled ? bundleData.contractTrialInfo.trialPeriod : bundleData.contractInfo.contractPeriod || 1;

  const displayPeriod = voucher?.offerInfo?.lowStart?.numberOfPeriods
    ? parseInt(String(voucher.offerInfo.lowStart.numberOfPeriods), 10)
    : parseInt(String(trialPeriod), 10);

  const sumMultiplicator = displayPeriod > 0 ? displayPeriod : 1;

  const displayPrice = voucher?.offerInfo?.lowStart?.percentage
    ? sumGrossAmount - (sumGrossAmount / 100) * voucher.offerInfo.lowStart.percentage
    : trialEnabled && (parseFloat(String(sumTrialGrossAmount)) || sumTrialGrossAmount === 0)
      ? sumTrialGrossAmount * sumMultiplicator
      : sumGrossAmount * sumMultiplicator;

  const periodText = getNumeralPrefix(trialUnit, displayPeriod);

  // const displayPrice =  discountPrice && parseInt(discountPrice, 10)
  //     ? discountPrice
  //     : sumTrialGrossAmount;

  const priceFragment =
    parseFloat(String(displayPrice)) === 0 || Math.abs(displayPrice) === 0
      ? 'gratis'
      : trialPeriod > 1 && !voucher
        ? `um einmalig ${formatPrice(String(displayPrice), true)}`
        : `um ${formatPrice(String(displayPrice), true)}`;

  const followPeriodText = voucher?.offerInfo?.lowStart.closeSubOnExpiry
    ? 'endet das Abo automatisch'
    : `${formatPrice(String(sumGrossAmount), true)} pro ${getTrialPeriod(trialUnit)}`;

  return displayPrice === sumGrossAmount
    ? `Jetzt <strong>${priceFragment}</strong> pro ${getTrialPeriod(trialUnit)}`
    : `Jetzt <strong>${periodText} ${getTrialPeriod(trialUnit, displayPeriod)} ${priceFragment}</strong><br/> danach ${followPeriodText}`;
}

/**
 *
 * @param {string} trialUnit
 * @param {Number} displayPeriod
 * @returns {String}
 */
function getNumeralPrefix(trialUnit: string, displayPeriod: number) {
  if (parseInt(`${displayPeriod}`, 10) > 1) {
    return `die ersten ${displayPeriod}`;
  }

  // Plural cases will still result in singular form, since we don't have enough trust in MPP's API responses
  switch (trialUnit.toLowerCase()) {
    case 'minute':
    case 'minutes':
    case 'hour':
    case 'hours':
    case 'week':
    case 'weeks':
      return 'die erste';

    case 'day':
    case 'days':
    case 'month':
    case 'months':
      return 'der erste';

    case 'year':
    case 'years':
      return 'das erste';

    default:
      throw Error('Invalid trial unit specificied');
  }
}

/**
 *
 * @param {Bundle} bundleData
 * @returns {string} string url to product image
 */
function getProductImage(bundleData: Bundle) {
  if (bundleData?.serviceImage) {
    return bundleData.serviceImage;
  } else if (bundleData?.lineItems?.[0]?.product.imageLocation) {
    return bundleData.lineItems[0].product.imageLocation;
  }

  return '';
}

/**
 *
 * @param {Bundle} bundleData
 * @returns {string}
 */
function getProductName(bundleData: Bundle) {
  const productTitleExtended = bundleData?.contractCustomParameters?.find((item) => item.parameterName === 'ProductTitleExtended') || null;

  switch (true) {
    case Boolean(productTitleExtended):
      return productTitleExtended?.parameterValue ?? '';

    case bundleData?.name?.length > 0:
      return bundleData?.name ?? '';

    default:
      return '';
  }
}

/**
 *
 * @param {String} period
 * @returns {String}
 */
function setMinimumAmount(period: string) {
  return period === 'yearly' ? '25' : '3';
}

/**
 *
 * @param {Bundle} bundleData
 * @returns {Boolean}
 */
function isPur(bundleData: Bundle) {
  return bundleData && bundleData.contractSubscriptionGroup ? bundleData.contractSubscriptionGroup.toLowerCase() === 'pur' : false;
}

/**
 *
 * @param {Bundle} bundleData
 * @returns {Boolean | null}
 */
function isSupporter(bundleData: Bundle | null) {
  return bundleData && bundleData.contractSubscriptionGroup ? bundleData.contractSubscriptionGroup.toLowerCase() === 'supporter' : false;
}

/**
 *
 * @param {Bundle} bundleData
 * @returns {Boolean}
 */
function isSmart(bundleData: Bundle) {
  return bundleData && bundleData.contractSubscriptionGroup ? bundleData.contractSubscriptionGroup.toLowerCase() === 'smart' : false;
}

/**
 *
 * @param {Bundle} bundleData
 * @returns {Boolean}
 */
function isAnnualSubscription(bundleData: Bundle) {
  return bundleData?.contractSubscriptionGroup?.toLowerCase() === 'pur' && bundleData?.contractInfo?.contractUnit?.toLowerCase() === 'years';
}

/**
 *
 * @param {string}  text
 * @return {*}
 */
function setInnerHTML(text: string) {
  return { __html: text };
}

/**
 *
 * @param {Number} count
 * @returns {Boolean}
 */
function isSingle(count: number) {
  return parseInt(`${count}`, 10) === 1;
}

/**
 *
 * @param {String} pan
 * @returns {Boolean}
 */
function isValidPrintAccountNumber(pan: string) {
  return pan.length === 7 || pan.length === 8;
}

/**
 * Finds value by given key
 * @param {Object} obj
 * @param {String} key
 * @returns {*}
 */
function findValueByKey(obj: object, key: string) {
  let result = null;
  const recursivelyFindValueByKey = (o: Record<string, unknown>, key: string): void => {
    Object.keys(o).forEach((k) => {
      if (typeof o[k] === 'object' && o[k] !== null) {
        recursivelyFindValueByKey(o[k] as Record<string, unknown>, key);
      } else {
        if (k === key) result = o[k];
      }
    });
  };

  recursivelyFindValueByKey(obj as Record<string, unknown>, key);
  return result;
}

/**
 * Returns Date object for an offset year
 * @param {number} offsetYears
 * @param {Date} now
 * @returns {Date}
 */
function getOffsetDate(offsetYears: number, now: Date | null = null) {
  if (!now || !(now instanceof Date)) {
    now = new Date();
  }

  return new Date(now.setFullYear(now.getFullYear() + offsetYears));
}

/**
 * Posts message to payment iFrame
 * @param {unknown} payload
 * @param {String} selector
 */
function postMessage(payload: unknown, selector = '.payment-iframe iframe') {
  const iFrame = document.querySelector<HTMLIFrameElement>(selector)?.contentWindow;
  if (!iFrame) return;

  const allowedOrigin = new URL(import.meta.env.VITE_APP_PAYMENT_IFRAME_URL).origin;

  const targetOrigin = import.meta.env.NODE_ENV === 'development' ? '*' : allowedOrigin;

  log(`Posting message to ${targetOrigin}`, payload);

  iFrame.postMessage(payload, targetOrigin);
}

/**
 *
 * @param {String} urlString
 * @return {String}
 */
function addCacheBuster(urlString: string) {
  if (import.meta.env.NODE_ENV !== 'development') {
    return urlString;
  }

  const parsedUrl = new URL(urlString);
  const searchParams = new URLSearchParams(parsedUrl.search);
  const date = new Date();

  searchParams.set('cb', String(Math.floor(date.getTime() / 1000)));
  parsedUrl.search = searchParams.toString();

  return parsedUrl.toString();
}

/**
 * Checks validity of message origins, unless on localhost
 * @param {String} activeOrigin
 * @param {Boolean} allowInDevelopment
 */
function validateMessageOrigin(activeOrigin: string, allowInDevelopment: boolean = false) {
  if (allowInDevelopment && import.meta.env.VITE_APP_ENVIRONMENT === 'development') {
    log('Skipping validation for development environment');
    return;
  }

  // Make sure activeOrigins is of type origin and not any URL
  const allowedOrigins = [
    new URL(String(import.meta.env.VITE_APP_MPP_IFRAME_ORIGIN)).origin,
    new URL(String(import.meta.env.VITE_APP_PAYMENT_IFRAME_URL)).origin,
    window.location.origin
  ];

  log('Validating message origin', { activeOrigin, allowedOrigins });

  if (
    import.meta.env.NODE_ENV === 'production' &&
    !allowedOrigins.includes(activeOrigin) &&
    !activeOrigin.endsWith(`.${import.meta.env.VITE_APP_DST_ROOT_DOMAIN}`)
  ) {
    throw Error(`Origin mismatch, discarding message from ${activeOrigin}`);
  }
}

/**
 * Checks whether the card issuer requires gathering of device data in a hidden iFrame
 * @param {PurchaseResponse} result
 * @return {Boolean}
 */
function hasGatheringDeviceData(result: PurchaseResponse) {
  const output =
    result?.asynchronousProcessingParameters?.threeDSecureVersion?.toLowerCase() === 'v2' &&
    result?.asynchronousProcessingParameters?.threeDSecureStatus &&
    result.asynchronousProcessingParameters.threeDSecureStatus?.toLowerCase() === 'gatheringdevicedata';

  if (output) {
    log('3DS: Gathering device data is required', result.asynchronousProcessingParameters);
  }

  return output;
}

/**
 * Checks whether the card issuer requires a 3DS 1.0 challenge
 * @param {PurchaseResponse} result
 * @return {Boolean}
 */
function hasChallengeRequiredV1(result: PurchaseResponse) {
  const output =
    result?.asynchronousProcessingParameters?.threeDSecureVersion?.toLowerCase() === 'v1' &&
    result?.asynchronousProcessingParameters?.threeDSecureStatus &&
    result.asynchronousProcessingParameters.threeDSecureStatus?.toLowerCase() === 'redirectiontoauthsiterequired';

  if (output) {
    log('3DS: Challenge v1 is required', result.asynchronousProcessingParameters);
  }

  return output;
}

/**
 * Checks whether the card issuer requires a 3DS 2.0 challenge
 * @param {PurchaseResponse} result
 * @return {Boolean}
 */
function hasChallengeRequiredV2(result: PurchaseResponse) {
  const output =
    result?.asynchronousProcessingParameters?.threeDSecureVersion?.toLowerCase() === 'v2' &&
    result?.asynchronousProcessingParameters?.threeDSecureStatus &&
    result.asynchronousProcessingParameters.threeDSecureStatus?.toLowerCase() === 'challengerequired';

  if (output) {
    log('3DS: Challenge v2 is required', result.asynchronousProcessingParameters);
  }

  return output;
}

/**
 * Returns whether the current path is an error page impression
 * @returns {boolean}
 */
function isErrorPage() {
  return window.location.pathname.startsWith(config.routes.error);
}

/**
 * Get entitlements from idToken
 * @param {AuthSession} session
 * @returns {CognitoGroups[]}
 */
function getEntitlements(session: AuthSession): CognitoGroups[] {
  if (session?.idToken?.payload?.hasOwnProperty('cognito:groups')) {
    return session.idToken.payload['cognito:groups'];
  }

  return [];
}

/**
 * Returns whether the current user has a pur subscription
 * @returns {Promise<boolean>}
 */
async function userHasPur() {
  const session = await auth.getSession();

  if (!session) {
    return false;
  }

  return getEntitlements(session)!.includes('pur');
}

/**
 * @param {string} message
 * @returns {Promise<string>}
 */
async function digestMessage(message: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hash = await crypto.subtle.digest('SHA-256', data);
  // TODO: Research whether there's a modern way to do this, since the linter warns using btoa()
  const base64String = window.btoa(String.fromCharCode(...new Uint8Array(hash)));

  return base64String.substring(0, 16);
}

/**
 * Checks if a user is eligible for a discount (trialperiod) for a PUR subscription
 * Fetches all subscriptions of the user with getAccountSubscriptions and checks if a previous
 * subscription matches the given productGroup
 * @param {string} productGroup
 * @returns {Promise<boolean>} is user eligible for a discount (trial period)
 */
async function isEligibleForTrialPrice(productGroup: string): Promise<boolean> {
  try {
    const subscriptions: AccountSubscription[] = (await getAccountSubscriptions(false)).subscriptions;
    return !subscriptions.some((subscription) => subscription.contractInfo.group === productGroup);
  } catch (e: unknown) {
    // If the request fails, we assume the user is eligible for a trial price -> not sure if this is a correct approach
    errorLog(`Couldn't retrieve subscription history to check if user is eligible for trial price ${typeof e === 'object' && e && 'message' in e ? '' : e}`);
    return true;
  }
}

/**
 * Custom hook to check if the user can reuse their payment data
 * @returns {boolean} canReusePaymentData
 */
function useReusePaymentData() {
  const [state] = useContext(Context);

  const canReusePaymentData = () => (Object.values(reusablePaymentMethods) as Array<string>).includes((state.upgrade?.activePaymentMethod ?? '').toLowerCase());

  return canReusePaymentData();
}

/**
 * Checks if there is a valid country code and if it is different from the current one
 * @param {State} state
 * @param {string} currentCountryCode
 * @returns {boolean} hasNewCountryCode
 */
function hasNewCountryCode(state: State, currentCountryCode: string) {
  if (!(state.address?.delivery?.country || state.upgrade?.activeCountryCode)) {
    return false;
  }

  return [state.address?.delivery?.country, state.upgrade?.activeCountryCode].includes(currentCountryCode);
}

export {
  addCacheBuster,
  useReusePaymentData,
  digestMessage,
  findValueByKey,
  formatHeadline,
  getCustomParameter,
  getKeyByValue,
  getKeyByValueArray,
  getOffsetDate,
  getPricingText,
  getProductClass,
  getProductImage,
  getProductName,
  getUrlParams,
  getUTMParamsFromSessionStorage,
  hasChallengeRequiredV1,
  hasChallengeRequiredV2,
  hasGatheringDeviceData,
  hasNewCountryCode,
  isAnnualSubscription,
  isArray,
  isEligibleForTrialPrice,
  isErrorPage,
  isObject,
  isPur,
  isSmart,
  isString,
  isSupporter,
  isValidPrintAccountNumber,
  persistState,
  postMessage,
  range,
  saveUTMParamsInSessionStorage,
  setInnerHTML,
  setMinimumAmount,
  setSupporterAmount,
  setTitle,
  sortObject,
  userHasPur,
  validateMessageOrigin
};
